/**
 * DailyEmailPage.jsx — Nebula Finance AI
 * Reworked: daily AI caching, correct schema fields, direct Supabase fetches.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";

// ── CONFIG ────────────────────────────────────────────────────────────────────
const SUPABASE_ANON     = process.env.REACT_APP_SUPABASE_ANON_KEY;
const EDGE_FUNCTION_URL = process.env.REACT_APP_EDGE_FUNCTION_URL;

// Cache key — one AI brief per calendar day per company
const briefCacheKey = (companyId) =>
  `nebula_brief_${companyId}_${new Date().toISOString().slice(0, 10)}`;

// ── HELPERS ───────────────────────────────────────────────────────────────────
const parseAmt  = (v) => parseFloat((v || "").toString().replace(/[^0-9.]/g, "")) || 0;
const daysUntil = (d) => Math.round((new Date(d) - new Date()) / 86400000);

const fmtAmt = (n, currency = "INR") => {
  const symbols = { INR: "₹", USD: "$", EUR: "€", GBP: "£" };
  const sym = symbols[currency] || (currency + " ");
  const num = Number(n) || 0;
  if (currency === "INR") {
    if (num >= 10000000) return `${sym}${(num / 10000000).toFixed(2)}Cr`;
    if (num >= 100000)   return `${sym}${(num / 100000).toFixed(2)}L`;
    if (num >= 1000)     return `${sym}${(num / 1000).toFixed(2)}K`;
    return `${sym}${num.toFixed(0)}`;
  }
  if (num >= 1000000) return `${sym}${(num / 1000000).toFixed(2)}M`;
  if (num >= 1000)    return `${sym}${(num / 1000).toFixed(2)}K`;
  return `${sym}${num.toLocaleString()}`;
};

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
  : "—";

const todayStr = () => new Date().toLocaleDateString("en-IN", {
  weekday: "long", day: "2-digit", month: "long", year: "numeric",
});

// ── DARK PALETTE ──────────────────────────────────────────────────────────────
const D = {
  bg: "#0a0b0f", card: "#12141a", card2: "#1a1d26",
  border: "#2a2d3a", border2: "#1e2130",
  white: "#ffffff", text: "#f1f5f9", muted: "#94a3b8", dim: "#64748b",
  indigo: "#6366f1", indigoL: "#818cf8",
  indigoBg: "rgba(99,102,241,0.12)", indigoBr: "rgba(99,102,241,0.25)",
  blue: "#3b82f6", blueBg: "rgba(59,130,246,0.12)",
  green: "#4ade80", greenD: "#059669", greenBg: "rgba(74,222,128,0.1)",
  red: "#f87171", redD: "#dc2626", redBg: "rgba(248,113,113,0.1)",
  amber: "#fbbf24", amberD: "#d97706", amberBg: "rgba(251,191,36,0.1)",
  purple: "#c084fc", purpleBg: "rgba(192,132,252,0.1)",
  teal: "#2dd4bf", tealBg: "rgba(45,212,191,0.1)",
  brand: "#3b7ef8", brandBg: "linear-gradient(135deg, #3b7ef8 0%, #2563eb 100%)",
};

// ── FETCH AI MARKET BRIEF (once per day, cached in localStorage) ──────────────
async function fetchMarketBrief(companyId, profile, hedges, receivables, payables) {
  const cacheKey = briefCacheKey(companyId);
  const cached   = localStorage.getItem(cacheKey);
  if (cached) return cached; // already generated today — return immediately

  const activeRec   = (receivables || []).filter(r => r.status !== "Settled" && r.status !== "settled");
  const activePay   = (payables    || []).filter(p => p.status !== "Settled" && p.status !== "settled");
  const totalRec    = activeRec.reduce((s, r) => s + parseAmt(r.amount), 0);
  const totalPay    = activePay.reduce((s, p) => s + parseAmt(p.amount), 0);
  const activeHedgesForBrief = (hedges || []).filter(h => h.status === "Active" || h.status === "active");
const totalHedged = activeHedgesForBrief.reduce((s, h) => s + (parseFloat(h.notional_amount_num) || 0), 0);
  const hedgeRatio  = (totalRec + totalPay) > 0
    ? ((totalHedged / (totalRec + totalPay)) * 100).toFixed(1) : "0";

  const res = await fetch(EDGE_FUNCTION_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${SUPABASE_ANON}`,
    },
    body: JSON.stringify({
      system: `You are a senior treasury analyst writing a concise daily market brief for a corporate treasury team in India.
Write exactly 3-4 sentences covering: current USD/INR direction and key level to watch, RBI policy stance, one key global macro event relevant today, and one actionable implication for Indian corporates with FX exposure.
Be factual and professional. Write in flowing prose only — no bullet points, no headings, no greeting, no sign-off.`,
      max_tokens: 250,
      messages: [{
        role: "user",
        content: `Today is ${todayStr()}. Company sector: ${profile?.industry || "Corporate"}, region: ${profile?.region || "India"}. Hedge ratio: ${hedgeRatio}%, Net FX exposure: ${fmtAmt(Math.abs(totalRec - totalPay))}. Write the market brief paragraph.`,
      }],
    }),
  });

 if (!res.ok) throw new Error(`AI error ${res.status}`);
  const data = await res.json();
  const brief = (data.content || []).map(b => b.text || "").join("").trim();

  // Cache for the rest of today
  try { localStorage.setItem(cacheKey, brief); } catch (_) {}
  return brief;
}

// ── FETCH NEBULA INTELLIGENCE VIA EDGE FUNCTION ───────────────────────────────
async function fetchNebulaInsights(profile, hedges, receivables, payables) {
  const activeRec    = (receivables || []).filter(r => r.status !== "Settled" && r.status !== "settled");
  const activePay    = (payables    || []).filter(p => p.status !== "Settled" && p.status !== "settled");
const activeHedges = (hedges || []).filter(h => 
  h.status === "Active" || h.status === "active"
);

  // ── Keep every currency SEPARATE — never add across currencies ──
  const recByCcy = activeRec.reduce((m, r) => {
    const ccy = r.currency || "INR";
    m[ccy] = (m[ccy] || 0) + parseAmt(r.amount);
    return m;
  }, {});
  const payByCcy = activePay.reduce((m, p) => {
    const ccy = p.currency || "INR";
    m[ccy] = (m[ccy] || 0) + parseAmt(p.amount);
    return m;
  }, {});
  const hedgeByCcy = activeHedges.reduce((m, h) => {
    const pair = h.currency_pair || "USDINR";
    m[pair] = (m[pair] || 0) + (parseFloat(h.notional_amount_num) || 0);
    return m;
  }, {});

  // ── Per-currency net exposure (rec - pay), only where both exist ──
  const allCcy = new Set([...Object.keys(recByCcy), ...Object.keys(payByCcy)]);
  const netByCcy = {};
  allCcy.forEach(ccy => {
    const rec = recByCcy[ccy] || 0;
    const pay = payByCcy[ccy] || 0;
    netByCcy[ccy] = rec - pay;
  });

  // ── Format each currency block for the prompt ──
  const recLines = Object.entries(recByCcy)
    .map(([ccy, amt]) => `  ${ccy} ${fmtAmt(amt, ccy)} (${activeRec.filter(r => (r.currency || "INR") === ccy).length} invoices)`)
    .join("\n") || "  None";

  const payLines = Object.entries(payByCcy)
    .map(([ccy, amt]) => `  ${ccy} ${fmtAmt(amt, ccy)} (${activePay.filter(p => (p.currency || "INR") === ccy).length} invoices)`)
    .join("\n") || "  None";

  const netLines = Object.entries(netByCcy)
    .map(([ccy, net]) => `  ${ccy} ${fmtAmt(Math.abs(net), ccy)} ${net >= 0 ? "net receivable" : "net payable"}`)
    .join("\n") || "  None";

  const hedgeLines = Object.entries(hedgeByCcy)
    .map(([pair, amt]) => `  ${pair}: ${fmtAmt(amt, pair.replace("INR","").replace("inr",""))} notional`)
    .join("\n") || "  No active hedges";

  const res = await fetch(EDGE_FUNCTION_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${SUPABASE_ANON}`,
    },
    body: JSON.stringify({
      system: `You are Nebula Intelligence, an AI treasury advisor. Return ONLY a JSON array of 3-4 insight objects. Each object must have: title (string), insight (string, 1-2 sentences), action (string, one clear recommendation), severity ("critical"|"warning"|"info"), category ("fx_risk"|"hedging"|"liquidity"|"market"|"cash_flow"). No preamble, no markdown, just the raw JSON array. Always reference amounts with their correct currency symbol and never mix currencies.`,
      max_tokens: 800,
      messages: [{
        role: "user",
        content: `Company: ${profile?.company_name || "Corporate"}, Industry: ${profile?.industry || "N/A"}.

ACTIVE RECEIVABLES (by currency):
${recLines}

ACTIVE PAYABLES (by currency):
${payLines}

NET EXPOSURE (by currency, receivables minus payables):
${netLines}

ACTIVE HEDGE POSITIONS (by currency pair):
${hedgeLines}
Total hedge contracts: ${activeHedges.length}

NOTE: Treat each currency independently. Do not add or compare amounts across different currencies. Reference exact amounts and currencies in your insights.
Generate 3-4 treasury insights as a JSON array based on these exact numbers.`,
      }],
    }),
  });

  if (!res.ok) throw new Error(`Insights error ${res.status}`);
  const data  = await res.json();
  const raw   = (data.content || []).map(b => b.text || "").join("").trim();
  const clean = raw.replace(/```json|```/g, "").trim();
  return JSON.parse(clean);
}

// ── BUILD SECTIONS ────────────────────────────────────────────────────────────
function buildSections({ recipientName, marketBrief, receivables, payables, hedges, aiInsights }) {
  const now  = new Date(); now.setHours(0, 0, 0, 0);
  const in10 = new Date(now); in10.setDate(now.getDate() + 10);
  const in30 = new Date(now); in30.setDate(now.getDate() + 30);

  // Receivables due in next 10 days — uses party_name (correct schema)
  const recDue = (receivables || [])
    .filter(r => {
      const d = new Date(r.due_date);
      const isSettled = r.status === "Settled" || r.status === "settled";
      return !isSettled && d >= now && d <= in10;
    })
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));

  // Payables due in next 10 days
  const payDue = (payables || [])
    .filter(p => {
      const d = new Date(p.due_date);
      const isSettled = p.status === "Settled" || p.status === "settled";
      return !isSettled && d >= now && d <= in10;
    })
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));

  // Hedges maturing in next 30 days
  const maturing = (hedges || [])
    .filter(h => {
      const dateStr = h.maturity_date || h.expiry_date || h.value_date;
      if (!dateStr) return false;
      const d = new Date(dateStr);
      return d >= now && d <= in30;
    })
    .sort((a, b) => {
      const da = new Date(a.maturity_date || a.expiry_date || a.value_date);
      const db = new Date(b.maturity_date || b.expiry_date || b.value_date);
      return da - db;
    });

  // Rec/Pay summary paragraph
  let recPayPara = "";
  if (recDue.length === 0 && payDue.length === 0) {
    recPayPara = "No receivables or payables are due in the next 10 days. Your near-term cash flow position is clear.";
  } else {
    const parts = [];
    if (recDue.length > 0) {
      const total = recDue.reduce((s, r) => s + parseAmt(r.amount), 0);
      const list  = recDue.map(r =>
        `${r.party_name || "Party"} — ${r.currency || "INR"} ${fmtAmt(parseAmt(r.amount), r.currency || "INR")} due ${fmtDate(r.due_date)}`
      ).join("; ");
      parts.push(`${recDue.length} receivable${recDue.length > 1 ? "s" : ""} totalling ${fmtAmt(total)} due within the next 10 days: ${list}.`);
    }
    if (payDue.length > 0) {
      const total = payDue.reduce((s, p) => s + parseAmt(p.amount), 0);
      const list  = payDue.map(p =>
        `${p.party_name || "Party"} — ${p.currency || "INR"} ${fmtAmt(parseAmt(p.amount), p.currency || "INR")} due ${fmtDate(p.due_date)}`
      ).join("; ");
      parts.push(`${payDue.length} payable${payDue.length > 1 ? "s" : ""} totalling ${fmtAmt(total)} due: ${list}.`);
    }
    recPayPara = parts.join(" ");
    if (recDue.length > 0 && payDue.length > 0) {
      const net = recDue.reduce((s, r) => s + parseAmt(r.amount), 0)
                - payDue.reduce((s, p) => s + parseAmt(p.amount), 0);
      recPayPara += ` Net 10-day position: ${net >= 0 ? "+" : ""}${fmtAmt(Math.abs(net))} ${net >= 0 ? "inflow" : "outflow"}.`;
    }
  }

  // Maturity paragraph
  let maturityPara = "";
  if (maturing.length === 0) {
    maturityPara = "No hedge contracts are maturing in the next 30 days. The hedge book is stable with no immediate rollover decisions required.";
  } else {
    const urgent = maturing.filter(h => daysUntil(h.maturity_date || h.expiry_date || h.value_date) <= 7);
    const list   = maturing.map(h => {
      const dateStr = h.maturity_date || h.expiry_date || h.value_date;
      const days    = daysUntil(dateStr);
      return `${h.contract_reference || h.id?.slice(0, 8) || "Contract"} (${h.currency || ""} ${fmtAmt(h.notional_amount_num || h.notional_amount)}, expires ${fmtDate(dateStr)}, ${days}d)`;
    }).join("; ");
    maturityPara = `${maturing.length} hedge contract${maturing.length > 1 ? "s" : ""} maturing in the next 30 days: ${list}. `;
    maturityPara += urgent.length > 0
      ? `${urgent.length} contract${urgent.length > 1 ? "s" : ""} expire within 7 days — immediate rollover or settlement decision required.`
      : "Please review rollover or settlement options with your bank ahead of these maturity dates.";
  }

  return { recipientName, marketBrief, recPayPara, maturityPara, recDue, payDue, maturing, aiInsights };
}

// ── PLAIN TEXT FOR COPY ───────────────────────────────────────────────────────
function buildPlainText(s) {
  const lines = [
    `Hi ${s.recipientName},`, "",
    "MARKET BRIEF", s.marketBrief, "",
    "RECEIVABLES & PAYABLES — NEXT 10 DAYS", s.recPayPara,
  ];
  s.recDue.forEach(r => lines.push(`  [REC] ${r.party_name || "—"} | ${fmtAmt(parseAmt(r.amount), r.currency)} | Due: ${fmtDate(r.due_date)} | ${r.status}`));
  s.payDue.forEach(p => lines.push(`  [PAY] ${p.party_name || "—"} | ${fmtAmt(parseAmt(p.amount), p.currency)} | Due: ${fmtDate(p.due_date)} | ${p.status}`));
  lines.push("", "CONTRACT MATURITY", s.maturityPara);
  s.maturing.forEach(h => {
    const dateStr = h.maturity_date || h.expiry_date || h.value_date;
    lines.push(`  ${h.contract_reference || "—"} | ${fmtAmt(h.notional_amount_num || h.notional_amount)} | Expires: ${fmtDate(dateStr)} | ${daysUntil(dateStr)}d left`);
  });
  if (s.aiInsights?.length > 0) {
    lines.push("", "NEBULA INTELLIGENCE");
    s.aiInsights.forEach((ins, i) => {
      lines.push(`${i + 1}. [${(ins.severity || "").toUpperCase()}] ${ins.title}`);
      lines.push(`   ${ins.insight}`);
      lines.push(`   Action: ${ins.action}`);
    });
  }
  lines.push("", "—", "Nebula Finance AI", `Daily Treasury Brief · ${todayStr()}`);
  return lines.join("\n");
}

// ── SUB-COMPONENTS ────────────────────────────────────────────────────────────
function SectionHeader({ label, icon, accent = D.indigoL }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
      {icon && (
        <div style={{
          width: 28, height: 28, borderRadius: 7,
          background: `${accent}22`, border: `1px solid ${accent}44`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 13, flexShrink: 0,
        }}>{icon}</div>
      )}
      <span style={{
        fontSize: 11, fontWeight: 700, color: accent,
        textTransform: "uppercase", letterSpacing: "0.1em",
      }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: D.border }} />
    </div>
  );
}

function Pill({ label, color = D.indigoL, bg = D.indigoBg }) {
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
      background: bg, color, border: `1px solid ${color}44`,
      textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap",
    }}>{label}</span>
  );
}

function DarkTable({ headers, rows, emptyMsg }) {
  return (
    <div style={{ borderRadius: 8, overflow: "hidden", border: `1px solid ${D.border}`, marginTop: 12 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ background: D.card2 }}>
            {headers.map(h => (
              <th key={h} style={{
                padding: "8px 12px", textAlign: "left",
                fontSize: 9, fontWeight: 700, color: D.dim,
                textTransform: "uppercase", letterSpacing: "0.07em",
                borderBottom: `1px solid ${D.border}`,
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={headers.length} style={{
                padding: "16px 12px", textAlign: "center",
                color: D.dim, fontSize: 11, fontStyle: "italic",
              }}>{emptyMsg || "No items"}</td>
            </tr>
          ) : rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${D.border2}`, background: i % 2 === 0 ? D.card : "#0e1018" }}>
              {row.map((cell, j) => (
                <td key={j} style={{ padding: "9px 12px" }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── EMAIL PREVIEW ─────────────────────────────────────────────────────────────
function EmailPreview({ s }) {
  if (!s) return null;

  const CAT_COLORS = {
    fx_risk:      { label: "FX Risk",      color: "#fb923c", bg: "rgba(249,115,22,0.12)" },
    hedging:      { label: "Hedging",      color: D.indigoL, bg: D.indigoBg             },
    liquidity:    { label: "Liquidity",    color: D.teal,    bg: D.tealBg               },
    market:       { label: "Market",       color: D.purple,  bg: D.purpleBg             },
    counterparty: { label: "Counterparty", color: D.amber,   bg: D.amberBg             },
    cash_flow:    { label: "Cash Flow",    color: D.green,   bg: D.greenBg             },
  };

  return (
    <div style={{
      background: D.bg, borderRadius: 12, border: `1px solid ${D.border}`,
      overflow: "hidden", width: "100%",
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
    }}>
      {/* Header */}
      <div style={{
        background: D.card, borderBottom: `1px solid ${D.border}`,
        padding: "18px 28px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 10, background: D.brandBg,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, fontWeight: 900, color: D.white,
            boxShadow: "0 4px 12px rgba(59,126,248,0.35)", flexShrink: 0,
          }}>N</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: D.white }}>Nebula Finance AI</div>
            <div style={{ fontSize: 11, color: D.dim, marginTop: 1 }}>Investment & Services</div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: D.muted }}>{todayStr()}</div>
          <div style={{ marginTop: 4 }}>
            <Pill label="Daily Treasury Brief" color={D.indigoL} bg={D.indigoBg} />
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "28px 32px", display: "flex", flexDirection: "column", gap: 28 }}>

        <p style={{ fontSize: 15, color: D.text, margin: 0, fontWeight: 500 }}>
          Hi <span style={{ color: D.indigoL, fontWeight: 700 }}>{s.recipientName}</span>,
        </p>

        {/* Market Brief */}
        <div style={{
          background: D.card2, borderRadius: 10, border: `1px solid ${D.border}`,
          borderLeft: `3px solid ${D.indigoL}`, padding: "20px 22px",
        }}>
          <SectionHeader label="Market Brief — AI Generated" icon="📊" accent={D.indigoL} />
          <p style={{ fontSize: 13, color: D.muted, lineHeight: 1.8, margin: 0 }}>
            {s.marketBrief}
          </p>
          <p style={{ fontSize: 10, color: D.dim, margin: "10px 0 0", fontStyle: "italic" }}>
            ✦ Generated once today by Nebula AI · Cached until midnight
          </p>
        </div>

        {/* Receivables & Payables */}
        <div style={{
          background: D.card2, borderRadius: 10, border: `1px solid ${D.border}`,
          borderLeft: `3px solid ${D.green}`, padding: "20px 22px",
        }}>
          <SectionHeader label="Receivables & Payables — Next 10 Days" icon="💰" accent={D.green} />
          <p style={{ fontSize: 13, color: D.muted, lineHeight: 1.8, margin: 0 }}>{s.recPayPara}</p>
          <DarkTable
            headers={["Type", "Party", "Currency", "Amount", "Due Date", "Status"]}
            emptyMsg="No items due in the next 10 days"
            rows={[
              ...s.recDue.map((r) => [
                <Pill label="Receivable" color={D.green} bg={D.greenBg} />,
                <span style={{ color: D.text, fontSize: 12 }}>{r.party_name || "—"}</span>,
                <span style={{ color: D.dim, fontSize: 12 }}>{r.currency || "INR"}</span>,
                <span style={{ color: D.text, fontWeight: 600, fontSize: 12 }}>{fmtAmt(parseAmt(r.amount), r.currency || "INR")}</span>,
                <span style={{ color: D.muted, fontSize: 12 }}>{fmtDate(r.due_date)}</span>,
                <Pill label={r.status} color={r.status?.toLowerCase() === "overdue" ? D.red : D.green} bg={r.status?.toLowerCase() === "overdue" ? D.redBg : D.greenBg} />,
              ]),
              ...s.payDue.map((p) => [
                <Pill label="Payable" color={D.red} bg={D.redBg} />,
                <span style={{ color: D.text, fontSize: 12 }}>{p.party_name || "—"}</span>,
                <span style={{ color: D.dim, fontSize: 12 }}>{p.currency || "INR"}</span>,
                <span style={{ color: D.text, fontWeight: 600, fontSize: 12 }}>{fmtAmt(parseAmt(p.amount), p.currency || "INR")}</span>,
                <span style={{ color: D.muted, fontSize: 12 }}>{fmtDate(p.due_date)}</span>,
                <Pill label={p.status} color={D.amber} bg={D.amberBg} />,
              ]),
            ]}
          />
        </div>

        {/* Contract Maturity */}
        <div style={{
          background: D.card2, borderRadius: 10, border: `1px solid ${D.border}`,
          borderLeft: `3px solid ${D.amber}`, padding: "20px 22px",
        }}>
          <SectionHeader label="Contract Maturity — Next 30 Days" icon="📅" accent={D.amber} />
          <p style={{ fontSize: 13, color: D.muted, lineHeight: 1.8, margin: 0 }}>{s.maturityPara}</p>
          <DarkTable
            headers={["Contract Ref", "Currency", "Notional", "Maturity Date", "Days Left", "Action"]}
            emptyMsg="No hedge contracts maturing in the next 30 days"
            rows={s.maturing.map((h) => {
              const dateStr = h.maturity_date || h.expiry_date || h.value_date;
              const days    = daysUntil(dateStr);
              const urgent  = days <= 7;
              return [
                <span style={{ color: D.indigoL, fontWeight: 700, fontSize: 12 }}>{h.contract_reference || h.id?.slice(0, 8) || "—"}</span>,
                <span style={{ color: D.dim, fontSize: 12 }}>{h.currency || "—"}</span>,
                <span style={{ color: D.text, fontSize: 12 }}>{fmtAmt(h.notional_amount_num || h.notional_amount)}</span>,
                <span style={{ color: D.muted, fontSize: 12 }}>{fmtDate(dateStr)}</span>,
                <span style={{ color: urgent ? D.red : D.amber, fontWeight: urgent ? 800 : 400, fontSize: 12 }}>{days}d</span>,
                <Pill label={urgent ? "URGENT" : "Review"} color={urgent ? D.red : D.amber} bg={urgent ? D.redBg : D.amberBg} />,
              ];
            })}
          />
        </div>

        {/* Nebula Intelligence */}
        <div style={{
          background: D.card2, borderRadius: 10, border: `1px solid ${D.border}`,
          borderLeft: `3px solid ${D.purple}`, padding: "20px 22px",
        }}>
          <SectionHeader label="Nebula Intelligence" icon="✦" accent={D.purple} />

          {s.aiInsights?.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {s.aiInsights.map((insight, i) => {
                const cat      = CAT_COLORS[insight.category] || CAT_COLORS.fx_risk;
                const sevColor = insight.severity === "critical" ? D.red : insight.severity === "warning" ? D.amber : D.indigoL;
                const sevBg    = insight.severity === "critical" ? D.redBg : insight.severity === "warning" ? D.amberBg : D.indigoBg;
                return (
                  <div key={i} style={{
                    background: D.bg, borderRadius: 8,
                    border: `1px solid ${D.border}`,
                    borderLeft: `3px solid ${sevColor}`,
                    padding: "14px 16px",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <Pill label={cat.label} color={cat.color} bg={cat.bg} />
                      <Pill label={insight.severity} color={sevColor} bg={sevBg} />
                    </div>
                    <p style={{ fontSize: 13, fontWeight: 700, color: D.text, margin: "0 0 6px", lineHeight: 1.4 }}>
                      {insight.title}
                    </p>
                    <p style={{ fontSize: 12, color: D.muted, margin: "0 0 10px", lineHeight: 1.65 }}>
                      {insight.insight}
                    </p>
                    <div style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      background: D.indigoBg, border: `1px solid ${D.indigoBr}`,
                      borderRadius: 6, padding: "4px 12px",
                    }}>
                      <span style={{ color: D.indigoL, fontSize: 11, fontWeight: 600 }}>→ {insight.action}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "20px 0", color: D.dim, fontSize: 12, fontStyle: "italic" }}>
              Fetching Nebula Intelligence…
            </div>
          )}
        </div>

        {/* Sign-off */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          paddingTop: 16, borderTop: `1px solid ${D.border}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 9, background: D.brandBg,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 16, fontWeight: 900, color: D.white, flexShrink: 0,
            }}>N</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: D.white }}>Nebula Finance AI</div>
              <div style={{ fontSize: 10, color: D.dim }}>Investment & Services</div>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 10, color: D.dim }}>Automated Daily Treasury Brief</div>
            <div style={{ fontSize: 10, color: D.dim, marginTop: 2 }}>{todayStr()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MAIN EXPORT ───────────────────────────────────────────────────────────────
export default function DailyEmailPage({ profile, companyId }) {
  const [recipients,  setRecipients]  = useState([{ name: "Treasury Manager", email: "" }]);
  const [newName,     setNewName]     = useState("");
  const [newEmail,    setNewEmail]    = useState("");
  const [sections,    setSections]    = useState(null);
  const [generating,  setGenerating]  = useState(false);
  const [copied,      setCopied]      = useState(false);
  const [error,       setError]       = useState(null);
  const [previewIdx,  setPreviewIdx]  = useState(0);
  const [editField,   setEditField]   = useState(null);
  const [editText,    setEditText]    = useState("");

  // ── Fetch live data directly — don't rely on props ───────────────────────
  const [receivables, setReceivables] = useState([]);
  const [payables,    setPayables]    = useState([]);
  const [hedges,      setHedges]      = useState([]);
  const [dataReady,   setDataReady]   = useState(false);

  useEffect(() => {
    if (!companyId) return;
    async function loadData() {
      try {
        const [recRes, payRes, hedgeRes] = await Promise.all([
          supabase.from("receivables").select("*").eq("company_id", companyId),
          supabase.from("payables").select("*").eq("company_id", companyId),
          supabase.from("hedge_positions").select("*").eq("company_id", companyId),
        ]);
        setReceivables(recRes.data || []);
        setPayables(payRes.data || []);
        setHedges(hedgeRes.data || []);
        setDataReady(true);
      } catch (e) {
        console.error("Data load error:", e);
        setDataReady(true); // proceed anyway
      }
    }
    loadData();
  }, [companyId]);

  // ── Generate email once data is ready ────────────────────────────────────
  const generate = useCallback(async (recipientIdx = 0) => {
    if (!dataReady) return;
    setGenerating(true);
    setError(null);
    const recipientName = recipients[recipientIdx]?.name || "Team";

    try {
      // 1. Market brief — cached, so only calls AI once per day
      const marketBrief = await fetchMarketBrief(companyId, profile, hedges, receivables, payables);

      // 2. Nebula Intelligence — fresh each generate
      let aiInsights = [];
      try {
        aiInsights = await fetchNebulaInsights(profile, hedges, receivables, payables);
      } catch (e) {
        console.warn("Insights fetch failed:", e.message);
      }

      setSections(buildSections({ recipientName, marketBrief, receivables, payables, hedges, aiInsights }));
    } catch (e) {
      console.warn("AI brief failed, using fallback:", e.message);
      setError("AI market brief unavailable — using standard fallback text.");
      const fallback = "USD/INR remains in focus as global markets assess the Federal Reserve's rate trajectory and RBI's policy stance. The rupee continues to trade in a range with RBI likely to intervene on sharp moves. Corporates with upcoming USD payables should consider locking in rates at current levels given elevated uncertainty in EM currencies.";
      setSections(buildSections({ recipientName, marketBrief: fallback, receivables, payables, hedges, aiInsights: [] }));
    } finally {
      setGenerating(false);
    }
  }, [dataReady, recipients, companyId, profile, hedges, receivables, payables]);

  // Auto-generate when data loads
  useEffect(() => {
    if (dataReady) generate(0);
  }, [dataReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopy = () => {
    if (!sections) return;
    navigator.clipboard.writeText(buildPlainText(sections)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleAddRecipient = () => {
    if (!newEmail.includes("@")) return;
    setRecipients(p => [...p, { name: newName || newEmail.split("@")[0], email: newEmail }]);
    setNewName(""); setNewEmail("");
  };

  const handleSaveEdit = () => {
    setSections(p => ({ ...p, [editField]: editText }));
    setEditField(null);
  };

  const inputSt = {
    padding: "8px 12px", borderRadius: 7, border: `1px solid ${D.border}`,
    fontSize: 12, color: D.text, outline: "none",
    width: "100%", boxSizing: "border-box", background: D.card2,
  };
  const btnSt = (bg, disabled = false) => ({
    padding: "8px 18px", borderRadius: 7, border: "none",
    background: disabled ? "#374151" : bg, color: D.white,
    fontSize: 12, fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
  });

  return (
    <div style={{ display: "flex", minHeight: "calc(100vh - 60px)", background: D.bg, fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>

      {/* ── LEFT SIDEBAR ── */}
      <div style={{
        width: 280, flexShrink: 0, background: D.card,
        borderRight: `1px solid ${D.border}`,
        padding: "20px 18px", display: "flex", flexDirection: "column", gap: 20,
        overflowY: "auto",
      }}>
        {/* Schedule card */}
        <div style={{
          background: D.indigoBg, borderRadius: 10, border: `1px solid ${D.indigoBr}`,
          padding: "14px 16px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 14 }}>🕘</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: D.indigoL, textTransform: "uppercase", letterSpacing: "0.08em" }}>Schedule</span>
          </div>
          <p style={{ fontSize: 13, color: D.text, margin: "0 0 4px", fontWeight: 700 }}>Daily at 9:00 AM IST</p>
          <p style={{ fontSize: 11, color: D.dim, margin: 0, lineHeight: 1.55 }}>
            AI market brief is generated once per day and cached. Copy the preview to send via Gmail or Outlook.
          </p>
        </div>

        {/* Cache status */}
        <div style={{
          background: D.card2, borderRadius: 8, border: `1px solid ${D.border}`,
          padding: "10px 12px", fontSize: 11, color: D.dim,
        }}>
          <span style={{ color: D.amber, fontWeight: 700 }}>⚡ AI Cache: </span>
          {localStorage.getItem(briefCacheKey(companyId))
            ? "Brief already generated today. Regenerate only reloads insights + data."
            : "No cached brief yet — will generate now."}
        </div>

        {/* Recipients */}
        <div>
          <p style={{ fontSize: 10, fontWeight: 700, color: D.dim, margin: "0 0 12px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            👥 Recipients
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
            {recipients.map((r, i) => (
              <div key={i}
                onClick={() => { setPreviewIdx(i); generate(i); }}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: previewIdx === i ? D.indigoBg : D.card2,
                  border: `1px solid ${previewIdx === i ? D.indigoBr : D.border}`,
                  borderRadius: 8, padding: "9px 12px", cursor: "pointer",
                }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: D.text }}>{r.name}</div>
                  <div style={{ fontSize: 10, color: D.dim, marginTop: 1 }}>{r.email || "No email set"}</div>
                </div>
                <button
                  onClick={e => { e.stopPropagation(); setRecipients(p => p.filter((_, j) => j !== i)); }}
                  style={{ background: "none", border: "none", cursor: "pointer", color: D.red, fontSize: 18, lineHeight: 1 }}
                >×</button>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            <input value={newName}  onChange={e => setNewName(e.target.value)}  placeholder="Name"          style={inputSt} />
            <input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="Email address" type="email" style={inputSt} />
            <button onClick={handleAddRecipient} style={{ ...btnSt(D.indigo), width: "100%", padding: "9px" }}>+ Add Recipient</button>
          </div>
        </div>

        {/* Edit sections */}
        {sections && (
          <div>
            <p style={{ fontSize: 10, fontWeight: 700, color: D.dim, margin: "0 0 10px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              ✏️ Edit Sections
            </p>
            {[
              { key: "marketBrief",  label: "Market Brief",           icon: "📊" },
              { key: "recPayPara",   label: "Rec & Pay Summary",      icon: "💰" },
              { key: "maturityPara", label: "Contract Maturity",      icon: "📅" },
            ].map(({ key, label, icon }) => (
              <div key={key} style={{ marginBottom: 7 }}>
                {editField === key ? (
                  <div>
                    <textarea value={editText} onChange={e => setEditText(e.target.value)}
                      rows={5} style={{ ...inputSt, border: `1px solid ${D.indigo}`, resize: "vertical" }} />
                    <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                      <button onClick={handleSaveEdit}           style={btnSt(D.greenD)}>Save</button>
                      <button onClick={() => setEditField(null)} style={btnSt("#374151")}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => { setEditField(key); setEditText(sections[key]); }}
                    style={{
                      width: "100%", padding: "9px 12px", borderRadius: 7,
                      border: `1px solid ${D.border}`, background: D.card2,
                      color: D.muted, fontSize: 11, cursor: "pointer",
                      textAlign: "left", display: "flex", alignItems: "center", gap: 7,
                    }}>
                    <span>{icon}</span> {label}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {error && (
          <div style={{
            background: D.redBg, border: `1px solid ${D.red}44`,
            borderRadius: 8, padding: "10px 12px", fontSize: 11, color: D.red, lineHeight: 1.5,
          }}>{error}</div>
        )}
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ flex: 1, padding: "24px 28px", overflowY: "auto", minWidth: 0 }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20,
        }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: D.text, margin: "0 0 3px" }}>Daily Email Preview</h2>
            <p style={{ fontSize: 11, color: D.dim, margin: 0 }}>
              Previewing for&nbsp;
              <span style={{ color: D.indigoL, fontWeight: 600 }}>{recipients[previewIdx]?.name || "Recipient"}</span>
              {recipients[previewIdx]?.email ? ` — ${recipients[previewIdx].email}` : ""}
            </p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => {
              // Force refresh: clear today's cache then regenerate
              localStorage.removeItem(briefCacheKey(companyId));
              generate(previewIdx);
            }} disabled={generating}
              style={{ ...btnSt(D.card2, generating), border: `1px solid ${D.border}`, color: generating ? D.dim : D.muted }}>
              {generating ? "⏳ Generating…" : "↻ Refresh Brief"}
            </button>
            <button onClick={handleCopy} disabled={!sections} style={btnSt(copied ? D.greenD : D.indigo, !sections)}>
              {copied ? "✓ Copied!" : "📋 Copy Email"}
            </button>
          </div>
        </div>

        {/* Loading */}
        {(generating && !sections) && (
          <div style={{ textAlign: "center", padding: "80px 0", color: D.dim }}>
            <div style={{
              width: 36, height: 36, border: `3px solid ${D.indigo}`,
              borderTopColor: "transparent", borderRadius: "50%",
              animation: "spin 0.8s linear infinite", margin: "0 auto 16px",
            }} />
            <p style={{ margin: 0, fontSize: 13 }}>
              {!dataReady ? "Loading treasury data…" : "Generating AI market brief…"}
            </p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {sections && <EmailPreview s={sections} />}

        {sections && (
          <div style={{
            marginTop: 16, padding: "12px 16px", background: D.card2,
            borderRadius: 9, border: `1px solid ${D.border}`,
            fontSize: 11, color: D.dim, display: "flex", alignItems: "center", gap: 10,
          }}>
            <span style={{ fontSize: 16 }}>💡</span>
            <span>
              Click <strong style={{ color: D.muted }}>Copy Email</strong> then paste into Gmail or Outlook.
              Use <strong style={{ color: D.muted }}>↻ Refresh Brief</strong> to force a new AI market brief (clears today's cache).
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
